<template>
  <!-- Overflow commented out as it was used as a temporary solution for CUNY - but CUNY decided to reduce number of columns so overflow is no longer an issue -->
  <!-- <div style="max-width: 58rem; overflow-x: scroll;" > -->
  <div
    v-if="shouldRender"
    :class="{ 'py-2 px-4': displayedInTable }"
  >
    <div
      v-if="sectionSchedulingRedesign"
      class="schedule-label flex justify-between"
    >
        <h3 class="field-label">Schedule</h3>
        <span
          class="color-theme-light hover:color-theme-base transition-colors duration-300 ease-in-out cursor-pointer"
          @click="toggleExpandAll"
        >{{ expandAll ? 'Collapse all' : 'Expand all' }}</span>
    </div>
    <div v-if="!courseId">Course Does Not Exist</div>
    <div v-else>
      <template v-if="sectionSchedulingRedesign">
        <BaseAccordion
          v-for="section in sections"
          :key="section.sectionNumber"
          :ref="`section-${section.sectionNumber}`"
          class="bg-gray-200 rounded mb-2"
        >
          <div class="p-2">
            {{ `Section ${section.sectionNumber}` }}
          </div>
          <template #content>
            <section class="px-4 py-2 bg-white border-l border-r border-b rounded">
              <dl
                v-for="fieldKey in visibleFields"
                :key="`field-${fieldKey}`"
                class="flex"
              >
                <dt class="w-1/4 font-semibold">{{ fields[fieldKey].label || decamelize(fieldKey) }}</dt>
                <dd>{{ getFieldValue(fieldKey, section) }}</dd>
              </dl>
            </section>
          </template>
        </BaseAccordion>
      </template>
      <template>
        <div class="row px-0">
          <div class="col-8 px-0">
            <FormSelect
              placeholder="Select a Term"
              style="max-width:400px"
              :value="selectedTermId"
              :config="{ options: terms.map(el => el.id) }"
              :custom-label="termDisplayName"
              :hide-empty-value="true"
              @input="selectedTermId = $event"
            />
          </div>
          <div v-if="isLoading" class="col-4 col-md-4 mt-4">
              Loading...
          </div>
        </div>

        <BaseTable v-if="!isLoading" :rows="sections" class="mt-4" resource-name="Sections" :clickable="false">
          <template #header v-if="sections.length">
            <th v-for="fieldKey in visibleFields" :key ="`field-heading-${fieldKey}`" class="th">{{fields[fieldKey].label || decamelize(fieldKey) }}</th>
          </template>
          <template #default="{ row: section }">
            <td v-for="fieldKey in visibleFields" :key ="`field-value-${fieldKey}`" class="bg-white">{{getFieldValue(fieldKey, section) }}</td>
          </template>
        </BaseTable>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import _sortBy from 'lodash/sortBy'
import { format, parseISO } from 'date-fns'
import FormSelect from '@/components/Fields/FormSelect'
import { formatNumberTimeToLocaleString } from '@/utils/dateAndTimeUtils'
import { decamelize } from '@/utils/generalUtils'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  HIDE_EMPTY_FIELDS,
  SECTION_SCHEDULING_REDESIGN,
  SM_IMPORT_WITH_RAW_COURSE_ID,
} from '@/constants/featureFlags'

const DEFAULT_FIELDS = {
  sectionNumber: {
    label: 'Section',
    hidden: false,
  },
  professors: {
    label: 'Instructors',
    hidden: false,
  },
  days: {
    label: 'Days',
    hidden: false,
  },
  times: {
    label: 'Times',
    hidden: false,
  },
  room: {
    label: 'Room',
    hidden: false,
  },
  enrollment: {
    label: 'Enrl.',
    hidden: false,
  },
  maxEnrollment: {
    label: 'Max Enrl.',
    hidden: false,
  },
  instructionMode: {
    label: 'Instruction Mode',
    hidden: false,
  },
  status: {
    label: 'Status',
    hidden: false,
  },
}

const WEEKDAYS_MAP = {
  0: 'Su',
  1: 'Mo',
  2: 'Tu',
  3: 'Wed',
  4: 'Thr',
  5: 'Fr',
  6: 'Sa',
}

export default {
  components: { FormSelect },
  props: {
    documentValue: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    displayedInTable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sections: [],
      rooms: {},
      professors: {},
      isLoading: true,
      selectedTermId: '',
      expandAll: false,
    }
  },
  setup() {
    const { hideEmptyFields, sectionSchedulingRedesign, enableSmImportWithRawCourseId } = useFeatureFlags({
      hideEmptyFields: HIDE_EMPTY_FIELDS,
      sectionSchedulingRedesign: SECTION_SCHEDULING_REDESIGN,
      enableSmImportWithRawCourseId: SM_IMPORT_WITH_RAW_COURSE_ID,
    })

    return { hideEmptyFields, sectionSchedulingRedesign, enableSmImportWithRawCourseId }
  },
  computed: {
    ...mapState('settings', ['school']),
    ...mapState('terms', ['terms', 'currentTerm']),
    fields() {
      if (this.config.fields && Object.keys(this.config.fields).length) {
        return this.config.fields
      } else return DEFAULT_FIELDS
    },
    visibleFields() {
      return Object.keys(this.fields).filter((fieldKey) => {
        const field = this.fields[fieldKey]
        if (!field) return false
        if (field.hidden === true) return false
        else return true
      })
    },
    courseId() {
      // courseId value must come from course.courseGroupId
      return this.documentValue && this.documentValue.courseGroupId
    },
    rawCourseId() {
      return this.documentValue && this.documentValue.customFields?.rawCourseId
    },
    selectedTerm() {
      return this.terms.find((term) => term.id === this.selectedTermId)
    },
    shouldRender() {
      return this.hideEmptyFields ? !this.isLoading : true
    },
  },
  watch: {
    selectedTerm() {
      if (this.selectedTerm && this.selectedTerm.id) {
        this.getPublicCourseSections()
      }
    },
  },
  async created() {
    this.isLoading = true
    await Promise.all([
      this.$store.dispatch('terms/fetchTerms', { school: this.school }),
      this.$store.dispatch('terms/fetchCurrentTerm', { school: this.school }),
    ])
    this.selectedTermId = this.currentTerm.id
    await this.getPublicCourseSections()
    this.isLoading = false
  },
  methods: {
    decamelize,
    async getPublicCourseSections() {
      if (!this.selectedTerm) return
      if (!this.selectedTerm.id) return
      this.isLoading = true
      let returnFields = this.visibleFields.join(',')
      if (returnFields.includes('room')) {
        returnFields += ',times'
      }
      if (returnFields.includes('days')) {
        returnFields += ',times,startDate,endDate'
      }
      try {
        const url =
          `/ca/${this.school}/sections/${this.selectedTerm.id}/${
            this.enableSmImportWithRawCourseId ? this.rawCourseId || this.courseId : this.courseId
          }?includeRelatedData=true&returnFields=${returnFields}`
        const data = await this.$axios.$get(url)
        if (data.sections)
          this.sections = _sortBy(data.sections, ['sectionNumber'])
        if (data.professors) this.professors = data.professors
        if (data.rooms) this.rooms = data.rooms
      } catch (e) {
        this.$bugsnag.notify(e)
        console.warn(e)
      }
      this.isLoading = false
    },
    getFieldValue(fieldKey, section) {
      switch (fieldKey) {
        case 'professors':
          return this.getProfessorNames(section.professors || [])
        case 'days':
          return this.getDayNames(section.times || [])
        case 'times':
          return this.getTimes(section.times || [])
        case 'room':
          return this.getRoomNames(section.times || [])
        case 'dates':
          return this.getDates(section)
        default:
          return section[fieldKey] || section[fieldKey] === 0
            ? section[fieldKey]
            : section[fieldKey] || '-'
      }
    },
    showfield(key) {
      return this.fields[key] && this.fields[key].hidden !== true
    },
    termDisplayName(termId) {
      if (!termId) return ''
      const term = this.terms.find((term) => term.id === termId) || {}
      return term.displayName || termId
    },
    getProfessorNames(professors = []) {
      if (professors.length === 0) return '-'
      return professors.map((p) => this.getProfessorName(p)).join(', ')
    },
    getRoomNames(times = []) {
      return times.map((t) => this.getRoomName(t.classroom)).join(', ')
    },
    getDayNames(days = []) {
      if (!days || !days.length) return '-'
      if (days.every((t) => !t.day || !t.day.length)) return '-'
      return days
        .map((t) => t.day.map((d) => WEEKDAYS_MAP[d]).join(', '))
        .join(', ')
    },
    getTimes(times = []) {
      if (!times || !times.length) return '-'
      if (times.every((t) => !t.start && !t.end)) return '-'
      return times
        .map((t) => `${this.formatTime(t.start)} - ${this.formatTime(t.end)}`)
        .join(', ')
    },
    getDates(section) {
      const times = (section.times || []).map((el) => {
        return {
          ...el,
          startDate: el.startDate || section.startDate,
          endDate: el.endDate || section.endDate,
        }
      })
      if (!times.length) return '-'
      if (times.every((t) => !t.startDate && !t.endDate)) return '-'
      return times
        .map(
          (t) =>
            `${this.formatDate(t.startDate)} - ${this.formatDate(t.endDate)}`
        )
        .join(', ')
    },
    formatTime(time) {
      if (time === '' || time === null || time === undefined) return 'Not Set'
      return formatNumberTimeToLocaleString(time)
    },
    formatDate(date) {
      return format(parseISO(date), 'MM/dd/yyyy')
    },
    getProfessorName(professorId) {
      const professor = this.professors[professorId]
      if (!professor) return '-'
      return `${professor.firstName} ${professor.lastName}`
    },
    getRoomName(roomId) {
      const room = this.rooms[roomId]
      if (!room) return '-'
      return room.displayName || '-'
    },
    toggleExpandAll() {
      this.expandAll = !this.expandAll
      this.sections.forEach((section) => {
        this.$refs[`section-${section.sectionNumber}`][0].setIsOpen(
          this.expandAll
        )
      })
    },
  },
}
</script>

<style lang="css" scoped>
.schedule-label {
  @apply mb-2;
}

@media (max-width: 576px) {
  .schedule-label {
    @apply mb-1;
  }
}
</style>
